/**
 * ChartingRelayCard — per-relay card on the site charting page.
 *
 * Composes the site dashboard's RelayCard shell (power quick-action, relay
 * title + load-type chip, load carousel) with a charting-specific body: a
 * per-load contract panel (active/upcoming contracts with add, edit, and
 * delete) in place of the schedule section, and a caller-supplied header
 * slot (the calendar-visibility toggle). Load/relay configuration is not
 * editable here — those affordances navigate to the site dashboard.
 *
 * Contains:
 * - ChartingRelayCard — the composed card (default export)
 */
import { Add, Delete, Edit, InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { isAfter, isEqual, parseISO } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import StatusChip from '@sophocles/components/StatusChip';
import {
  createRelayLoadContract,
  deleteRelayLoadContract,
  editRelayLoadContract,
} from '@sophocles/features/charting/api';
import { addToast } from '@sophocles/features/toast';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';
import { sitePath } from '@sophocles/router/paths';
import { parseApiError } from '@sophocles/utils/apiErrors';
import RelayCard from '@sophocles/views/sites/components/RelayCards/RelayCard';

import RelayLoadContractForm from '../forms/RelayLoadContractForm';

// The panel surfaces only the nearest contracts; the full history lives in
// the charting calendar below the cards.
const MAX_CONTRACTS_PER_LOAD = 3;

/**
 * Active/upcoming contracts for one load, soonest-starting first.
 *
 * `contract_end` is parsed as a local calendar date (parseISO reads a
 * date-only string as UTC, which shifts the day near midnight).
 */
function activeOrUpcomingContracts(contracts, now) {
  return contracts
    .filter(c => {
      const [year, month, day] = c.contract_end.split('-').map(Number);
      const endDay = new Date(year, month - 1, day);
      return isAfter(endDay, now) || isEqual(endDay, now);
    })
    .sort((a, b) => parseISO(a.contract_start) - parseISO(b.contract_start));
}

function ContractRow({ contract, onEdit, onDelete, t }) {
  const isReadOnly = contract.mapping_name != null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {contract.advertiser}
          {contract.override_end_date ? <StatusChip label={t('charting.override')} color="bad" sx={{ ml: 1 }} /> : null}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {contract.contract_start} - {contract.contract_end}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
        {isReadOnly ? (
          <IconButton size="small" onClick={onEdit}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        ) : (
          <>
            <IconButton size="small" onClick={onEdit}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onDelete}>
              <Delete fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
    </Box>
  );
}

function ChartingRelayCard({
  loading,
  relay,
  siteId,
  unit,
  siteTimeZone,
  params,
  siteIccid,
  headerExtras = null,
  onContractsChanged,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  // Today as midnight local time, for date-only comparisons.
  const today = new Date();
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [editContract, setEditContract] = useState(null);

  const loads = relay.loads ?? [];
  const contracts = loads.flatMap(load => load.contracts ?? []);

  // Load/relay setup lives on the dashboard; the carousel's tap/Setup
  // affordances route there. sitePath throws on a missing param, so degrade
  // to a no-op when the route context is incomplete.
  const canNavigateToDashboard = params?.cust_id != null && params?.sitegroup_id != null && params?.site_id != null;
  const handleNavigateToDashboard = canNavigateToDashboard
    ? () => navigate(sitePath({ cust_id: params.cust_id, sitegroup_id: params.sitegroup_id, site_id: params.site_id }))
    : () => {};

  const handleAddClick = load => {
    setSelectedLoad(load);
    setEditContract(null);
    setDialogOpen(true);
  };

  const handleEditClick = contract => {
    setSelectedLoad(loads.find(load => load.id === contract.relay_load));
    setEditContract(contract);
    setDialogOpen(true);
  };

  const handleDeleteClick = contract => {
    confirmDelete(contract, {
      entityLabel: t('charting.contract'),
      onConfirm: deletedContract =>
        dispatch(deleteRelayLoadContract(deletedContract.id))
          .unwrap()
          .then(() => {
            dispatch(addToast({ message: t('charting.contractDeleted'), level: 'success' }));
            onContractsChanged?.();
          })
          .catch(err => {
            console.error('Failed to delete contract', err);
            const { nonFieldMessages } = parseApiError(err, {
              knownFields: [],
              genericMessage: t('charting.contractDeleteFailed'),
            });
            dispatch(addToast({ message: nonFieldMessages.join(' '), level: 'error' }));
          }),
    });
  };

  const contractPanel =
    loads.length > 0 ? (
      <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.5, py: 1.25 }}>
        <Stack spacing={1.25}>
          {loads.map(load => {
            const loadContracts = activeOrUpcomingContracts(load.contracts ?? [], now);
            return (
              <Box key={load.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {load.name}
                  </Typography>
                  <Tooltip title={t('charting.addContract')}>
                    <IconButton size="small" onClick={() => handleAddClick(load)}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {loadContracts.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('charting.noContracts')}
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {loadContracts.slice(0, MAX_CONTRACTS_PER_LOAD).map(contract => (
                      <ContractRow
                        key={contract.id}
                        contract={contract}
                        onEdit={() => handleEditClick(contract)}
                        onDelete={() => handleDeleteClick(contract)}
                        t={t}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    ) : null;

  return (
    <>
      {deleteConfirmDialog}
      <RelayCard
        loading={loading}
        relay={relay}
        siteId={siteId}
        unit={unit}
        siteTimeZone={siteTimeZone}
        params={params}
        siteIccid={siteIccid}
        hasCharting
        usesCalendar={false}
        hideOptions
        headerExtras={headerExtras}
        onClickEditSchedule={handleNavigateToDashboard}
      >
        {contractPanel}
      </RelayCard>

      {selectedLoad != null && (
        <RelayLoadContractForm
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={async values => {
            let response;
            if (editContract) {
              response = await dispatch(editRelayLoadContract({ id: editContract.id, ...values })).unwrap();
            } else {
              response = await dispatch(createRelayLoadContract(values)).unwrap();
            }
            setDialogOpen(false);
            onContractsChanged?.();
            return response;
          }}
          loadId={selectedLoad.id}
          localTimeZone={siteTimeZone}
          contract={editContract}
          existingContracts={contracts.filter(c => c.relay_load != selectedLoad?.id)}
        />
      )}
    </>
  );
}

export default ChartingRelayCard;
